import React, { useState, useEffect } from 'react';
import { getProjects, deleteProject } from '../../services/projectService';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { PageLoading } from '../ui/Loading';
import { AlertCircle } from 'lucide-react';

// Import components
import ProjectStats from './ProjectStats';
import ProjectTable from './ProjectTable';

const ProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, project: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await getProjects();
      
      if (error) throw error;
      
      setProjects(data || []);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };



  const handleDelete = (project) => {
    setDeleteDialog({ open: true, project });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.project) return;

    try {
      setLoading(true);
      const { error } = await deleteProject(deleteDialog.project.id);
      
      if (error) throw error;
      
      toast.success('ลบโปรเจคเรียบร้อยแล้ว');
      await loadProjects();
    } catch (err) {
      console.error('Error deleting project:', err);
      toast.error('เกิดข้อผิดพลาดในการลบโปรเจค');
    } finally {
      setLoading(false);
      setDeleteDialog({ open: false, project: null });
    }
  };


  if (loading) {
    return <PageLoading message="กำลังโหลดรายการโปรเจค..." />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Alert className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            เกิดข้อผิดพลาด: {error}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <ProjectStats projects={projects} />


      {/* Projects Table */}
      <ProjectTable 
        projects={projects} 
        onDelete={handleDelete}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={() => setDeleteDialog({ open: false, project: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ยืนยันการลบโปรเจค</DialogTitle>
            <DialogDescription>
              คุณต้องการลบโปรเจค "{deleteDialog.project?.project_name}" หรือไม่?
              การกระทำนี้ไม่สามารถย้อนกลับได้
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              onClick={() => setDeleteDialog({ open: false, project: null })}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              ยกเลิก
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              ลบ
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectList;
